import React, { useMemo, useState } from "react";
import { Box, CircularProgress, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { Download, Pencil, Plus, Search, Trash2, Upload } from "lucide-react";
// import IconMegaphone from "@components/CustomIcons/IconMegaphone";
import AnimationFadeIn from "@components/AnimationFadeIn";
import Breadcrumb from "@components/Breadcrumb";
import InputCst from "@components/InputCst/InputCst";
import TabsCst from "@components/TabsCst";
import ListItemCardCst from "@components/ListItemCardCst";
import PaginatorCst from "@components/PaginatorCst";
import FiltersAccordionToggle from "@components/FiltersAccordionCst/FiltersAccordionToggle";
import { usePaginatedList } from "@hooks/usePaginatedList";
import { useFetchDetail } from "@hooks/useFetchDetail";
import { ServicesURL, downloadFile, uploadFile } from "@utils/utilsApi";
import { MethodType } from "src/services/type";
import UploadModalCst from "@components/UploadModalCst";
import { PagesID } from "@utils/utilsConfigurations";
import { mapEntityToCard } from "@pages/Contents/V2/Corporate/Catalog/mapEntityToCard";
import { mapAggregatedType } from "@pages/Contents/V2/Corporate/Catalog/types";
import { isClassroomActivity } from "@utils/cardActions";
import {
  formatEnrollmentDate,
  formatDate,
} from "@pages/Contents/V2/Corporate/Catalog/UsersAndGroups/config/enrollmentDates";
import { normalizeEnrolledLo } from "../normalizeEnrolledLo";
import { downloadGroupLoCsv } from "../downloadGroupLoCsv";
import { buildGroupLoTabs, buildGroupLoTypeFilter } from "../groupLoTabs";
import { formatEnrollTypeLabel } from "@pages/Contents/V2/Corporate/Catalog/UsersAndGroups/config/enrollTypeMap";
import { buildExpirationBadge } from "@pages/Contents/V2/Corporate/Catalog/UsersAndGroups/config/expirationBadge";
import {
  buildConfigFromEnrollment,
  disabledByPriority,
} from "@pages/Contents/V2/Corporate/Catalog/UsersAndGroups/components/EditEnrollmentModal";
// import SolicitConfirmDialog, {
// SolicitTarget,
// } from "@pages/Contents/V2/Corporate/Catalog/UsersAndGroups/components/SolicitConfirmDialog";
import { PERMISSIONS, usePermissions } from "@utils/permissions";
import ConfirmDialogCst from "@components/ConfirmDialogCst";
import OutcomeModalV2Cst from "@components/OutcomeModalV2Cst";
import EnrollLOModal from "@pages/Contents/V2/Corporate/UsersGroups/components/EnrollLOModal";
import { fetcher } from "src/services/config";
import { openToast } from "@utils/toastStore";
import { mapUserToCard } from "../mapUserToCard";
import { useGroupMembersDetails } from "../useGroupMembersDetails";
import AddUsersToGroupModal from "../components/AddUsersToGroupModal";
import { useScrollToHash } from "@hooks/useScrollToHash";
import { LO_SECTION_ANCHOR, SECTION_ANCHOR_SCROLL_MARGIN } from "../types";
import { ButtonPrimary } from "src/UI/ButtonPrimary";
import { ButtonSecondary } from "src/UI/ButtonSecondary";
import PageHeaderV2Cst from "@components/PageHeaderV2Cst";

const PAGE_SIZE = 10;

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const theme = useTheme();
  return (
    <Typography
      sx={{
        fontSize: "20px",
        fontWeight: 700,
        color: theme.palette.primary.main,
      }}
    >
      {children}
    </Typography>
  );
};

const StaticGroupDetail: React.FC = () => {
  const { t } = useTranslation("group-detail-v2");
  const { t: tCatalog } = useTranslation("catalog-v2");
  const { t: tWizard } = useTranslation("group-wizard-v2");
  const { t: tInput } = useTranslation("input");
  const { t: tEnroll } = useTranslation("users-and-groups");
  const theme = useTheme();
  const navigate = useNavigate();
  const { id: corporateId = "", groupId = "" } = useParams();
  const { can } = usePermissions();
  const canUpdateEnrollment = can(PERMISSIONS.enrollment.update);
  // Aggiunta e caricamento membri richiedono la scrittura sul gruppo, come
  // l'azione "Aggiungi utenti" nella barra azioni dell'elenco.
  const canWriteGroup = can(PERMISSIONS.groups.static.write);
  const canReadGroup = can(PERMISSIONS.groups.static.read);

  const { dataDetail: groupDetail } = useFetchDetail(
    `${ServicesURL.staticGroups}/static`,
    groupId,
    PagesID["corporate.groups"],
  );

  useScrollToHash(!!groupDetail);

  // Sezione "Iniziative associate" nascosta
  // const initiativesUrl = `${ServicesURL.getEnrollmentMassive}?corporateId=${corporateId}&groupId=${groupId}`;
  // const { items: initiatives, isLoading: loadingInit } = usePaginatedList(
  //   initiativesUrl,
  //   { page: 1, pageSize: PAGE_SIZE },
  // );

  const [addUsersOpen, setAddUsersOpen] = useState(false);

  const [usersPage, setUsersPage] = useState(1);
  const [usersPageSize, setUsersPageSize] = useState(PAGE_SIZE);
  // const [usersSearch, setUsersSearch] = useState("");
  const [usersFilters, setUsersFilters] = useState<Record<string, any>>({});
  const [csvOpen, setCsvOpen] = useState(false);
  const usersUrl = `${ServicesURL.staticGroups}/v3/${corporateId}/static/${groupId}/users`;
  const {
    items: users,
    totalItems: usersTotal,
    totalPages: usersPages,
    isLoading: loadingUsers,
    mutate: mutateUsers,
  } = usePaginatedList(
    usersUrl,
    {
      page: usersPage,
      pageSize: usersPageSize,
      // search: usersSearch || undefined,
      fieldFilters: usersFilters,
    },
    { responseKey: "response" },
  );

  const usersPageIds = useMemo(
    () => users.map((u: any) => u.user_id ?? u.id_user ?? u.id),
    [users],
  );
  const { detailsById: userDetailsById } = useGroupMembersDetails(usersPageIds);

  const [loTab, setLoTab] = useState("all");
  const [loPage, setLoPage] = useState(1);
  const [loPageSize, setLoPageSize] = useState(PAGE_SIZE);
  const [loSearch, setLoSearch] = useState("");
  // P35 (allineare V2 a v1): LO del gruppo visibili e caricati solo con
  // learning.enrollment.read + learning_object.corporate.visibility, come la
  // route v1 ":idGroup/learning-objects" (corporate && corporate.catalog &&
  // learning.enrollment.read; corporate è già richiesto dalla route V2).
  const canViewGroupLo = can([
    PERMISSIONS.enrollment.read,
    PERMISSIONS.catalog.corporateVisibility,
  ]);
  const loUrl = canViewGroupLo
    ? `${ServicesURL.getLOEnrolledWithEnrollInfo
        .replace(":corporateId", corporateId)
        .replace(":groupId", groupId)}?isStandalone=true`
    : null;
  const {
    items: los,
    totalItems: loTotal,
    totalPages: loPages,
    isLoading: loadingLo,
    mutate: mutateLos,
  } = usePaginatedList(loUrl, {
    page: loPage,
    pageSize: loPageSize,
    search: loSearch || undefined,
    searchField: "loTitle",
    fieldFilters: buildGroupLoTypeFilter(loTab),
  });

  const [deleteLoTarget, setDeleteLoTarget] = useState<any>(null);
  const [isDeletingLo, setIsDeletingLo] = useState(false);
  const [showPartialDeleteInfo, setShowPartialDeleteInfo] = useState(false);
  const warnDeleteLoProgress =
    !!deleteLoTarget &&
    (mapAggregatedType(deleteLoTarget.type) !== "activity" ||
      isClassroomActivity(
        deleteLoTarget.type || deleteLoTarget.learning_object_type,
      ));
  const [editGroupLoTarget, setEditGroupLoTarget] = useState<any>(null);
  const [groupEnrollSource, setGroupEnrollSource] = useState<any>(null);
  // const [solicitTarget, setSolicitTarget] = useState<SolicitTarget | null>(
  // null,
  // );
  // const canSolicit = can(PERMISSIONS.enrollment.reminderMassive);

  // const openSolicit = (entity: any) => {
  // if (!canSolicit) return;
  // setSolicitTarget({
  // scope: "group",
  // name: groupDetail?.name || `ID ${groupId}`,
  // corporateId,
  // objectId: entity.id,
  // objectType: mapAggregatedType(entity.type),
  // groupId,
  // groupType: "STATIC",
  // });
  // };
  const [isLoadingGroupEnrollment, setIsLoadingGroupEnrollment] =
    useState(false);
  const [isDownloadingLoCsv, setIsDownloadingLoCsv] = useState(false);

  const handleDownloadLoCsv = async () => {
    if (!loUrl) return;
    setIsDownloadingLoCsv(true);
    try {
      await downloadGroupLoCsv(loUrl, groupId, t, loSearch.trim());
    } catch (error) {
      console.error(error);
      openToast({
        severity: "error",
        message: String(t("learningObjects.csvError")),
      });
    } finally {
      setIsDownloadingLoCsv(false);
    }
  };

  const confirmDeleteLo = async () => {
    if (!deleteLoTarget || !canUpdateEnrollment) return;
    setIsDeletingLo(true);
    try {
      const url = `${ServicesURL.postEnrollmentMassive}?corporateId=${corporateId}&groupId=${groupId}&groupType=STATIC&objectId=${deleteLoTarget.id}`;
      const res = await fetcher(url, MethodType.DELETE);
      setDeleteLoTarget(null);
      mutateLos();
      setTimeout(() => mutateLos(), 1500);
      setTimeout(() => mutateLos(), 4000);
      if (res?.usersAlreadyCompleted === true) {
        setShowPartialDeleteInfo(true);
      } else {
        openToast({
          severity: "success",
          message: String(tEnroll("card.delete-confirm.success")),
        });
      }
    } catch (error) {
      console.error(error);
      openToast({
        severity: "error",
        message: String(tEnroll("card.delete-confirm.error")),
      });
    } finally {
      setIsDeletingLo(false);
    }
  };

  const openGroupEnrollmentEditor = async () => {
    if (!editGroupLoTarget) return;
    setIsLoadingGroupEnrollment(true);
    try {
      const url = `${ServicesURL.getEnrollment}?corporateId=${corporateId}&objectId=${editGroupLoTarget.id}&massiveGroupId=${groupId}&groupType=STATIC&pageIndex=0&pageSize=20`;
      const res = await fetcher(url, MethodType.GET);
      const record = res?.enrollmentResponses?.[0];
      setGroupEnrollSource({
        ...editGroupLoTarget,
        ...record,
        id: editGroupLoTarget.id,
        title: editGroupLoTarget.title,
        entityType: mapAggregatedType(editGroupLoTarget.type),
      });
      setEditGroupLoTarget(null);
    } catch (error) {
      console.error(error);
      openToast({
        severity: "error",
        message: String(tEnroll("card.edit-group-confirm.fetch-error")),
      });
    } finally {
      setIsLoadingGroupEnrollment(false);
    }
  };

  const loTabs = buildGroupLoTabs(t);

  const searchBoxSx = {
    flex: 1,
    minWidth: 220,
    "& .MuiFormControl-root": {
      minWidth: "0 !important",
      width: "100% !important",
    },
    "& .MuiOutlinedInput-root": { borderRadius: "8px" },
    "& .MuiOutlinedInput-notchedOutline": { borderRadius: "8px" },
  };

  return (
    <AnimationFadeIn>
      <Box
        sx={{
          py: 3,
          pr: 3,
          display: "flex",
          flexDirection: "column",
          gap: 4,
        }}
      >
        <PageHeaderV2Cst>
          <Breadcrumb sticky={false} />
          <Typography
            sx={{ fontSize: "13px", color: theme.palette.text.primary, mt: 1 }}
          >
            ID: {groupId}
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              mt: 2,
            }}
          >
            <Typography sx={{ fontSize: "28px", fontWeight: 700 }}>
              {groupDetail?.name || "-"}
            </Typography>
            {groupDetail?.description && (
              <Typography
                sx={{
                  fontSize: "14px",
                  color: theme.palette.text.primary,
                  opacity: 0.8,
                }}
              >
                {groupDetail.description}
              </Typography>
            )}
          </Box>
        </PageHeaderV2Cst>

        {/* TODO Sezione "Iniziative associate" nascosta */}
        {/*
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <SectionTitle>{t("initiatives.title")}</SectionTitle>
            <Button
              variant="contained"
              startIcon={<Plus size={16} />}
              sx={{
                textTransform: "none",
                borderRadius: "8px",
                fontWeight: 700,
              }}
            >
              {t("initiatives.associate")}
            </Button>
          </Box>
          {loadingInit ? (
            <CircularProgress />
          ) : initiatives.length === 0 ? (
            <Typography sx={{ color: theme.palette.text.primary }}>
              {t("initiatives.empty")}
            </Typography>
          ) : (
            initiatives.map((row: any, index: number) => (
              <ListItemCardCst
                key={row.id ?? index}
                title={row.name || row.initiativeName || t("initiatives.item")}
                subtitle={{ id: row.id ?? row.enrollmentId }}
                badges={[]}
                actions={[]}
              />
            ))
          )}
        </Box>
        */}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <SectionTitle>{t("users.title")}</SectionTitle>
            <Box sx={{ display: "flex", gap: 2 }}>
              <ButtonPrimary
                size="md"
                startIcon={<Plus size={16} />}
                onClick={() => setAddUsersOpen(true)}
                disabled={!canWriteGroup}
              >
                {t("users.addUser")}
              </ButtonPrimary>
              <ButtonSecondary
                size="md"
                startIcon={<Download size={16} />}
                onClick={() =>
                  downloadFile(
                    `${ServicesURL.staticGroups}/static/${groupId}/users/external-id`,
                  )
                }
                disabled={!canReadGroup}
              >
                {t("downloadCsv")}
              </ButtonSecondary>
              <ButtonSecondary
                size="md"
                startIcon={<Upload size={16} />}
                onClick={() => setCsvOpen(true)}
                disabled={!canWriteGroup}
              >
                {t("bulk.uploadUsers", { ns: "users-groups-v2" })}
              </ButtonSecondary>
            </Box>
          </Box>

          {/* <Box sx={searchBoxSx}>
            <InputCst
              id="group-detail-users-search"
              placeholder={t("search")}
              inputSearch
              value={usersSearch}
              onChange={(e: any) => {
                setUsersSearch(e.target.value);
                setUsersPage(1);
              }}
              startAdornment={
                <Search
                  size={16}
                  color={theme.palette.systemSecondary01}
                  style={{ marginRight: 8 }}
                />
              }
            />
          </Box> */}

          <FiltersAccordionToggle
            align="right"
            pageId={PagesID["corporate.groups.user"]}
            value={usersFilters}
            onApply={(next) => {
              setUsersFilters(next);
              setUsersPage(1);
            }}
          />

          {loadingUsers ? (
            <CircularProgress />
          ) : users.length === 0 ? (
            <Typography sx={{ color: theme.palette.text.primary }}>
              {t("users.empty")}
            </Typography>
          ) : (
            users.map((u: any) => {
              const memberId = u.user_id ?? u.id_user ?? u.id;
              const normalizedUser = {
                ...u,
                ...userDetailsById.get(String(memberId)),
                id_user: memberId,
                external_id: u.external_id ?? u.externalId,
              };
              const cardProps = mapUserToCard(normalizedUser, {
                tInput,
                corporateId,
              });
              return (
                <ListItemCardCst
                  key={memberId}
                  {...cardProps}
                  actions={[
                    {
                      label: t("details"),
                      variant: "outlined",
                      onClick: () =>
                        navigate(
                          `/v2/corporate/${corporateId}/detail/users-groups/${memberId}`,
                        ),
                    },
                  ]}
                />
              );
            })
          )}

          {usersPages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <PaginatorCst
                page={usersPage}
                pageSize={usersPageSize}
                totalItems={usersTotal}
                onPageChange={setUsersPage}
                onPageSizeChange={(next) => {
                  setUsersPageSize(next);
                  setUsersPage(1);
                }}
              />
            </Box>
          )}
        </Box>

        {canViewGroupLo && (
          <Box
            id={LO_SECTION_ANCHOR}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              scrollMarginTop: SECTION_ANCHOR_SCROLL_MARGIN,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <SectionTitle>{t("learningObjects.title")}</SectionTitle>
              <ButtonSecondary
                size="md"
                startIcon={<Download size={16} />}
                onClick={handleDownloadLoCsv}
                disabled={isDownloadingLoCsv || loTotal === 0}
              >
                {t("downloadCsv")}
              </ButtonSecondary>
            </Box>

            <TabsCst
              tabs={loTabs}
              activeKey={loTab}
              onChange={(key) => {
                setLoTab(key);
                setLoPage(1);
              }}
            />

            <Box sx={searchBoxSx}>
              <InputCst
                id="group-detail-lo-search"
                placeholder={t("search")}
                inputSearch
                value={loSearch}
                onChange={(e: any) => {
                  setLoSearch(e.target.value);
                  setLoPage(1);
                }}
                startAdornment={
                  <Search
                    size={16}
                    color={theme.palette.systemSecondary01}
                    style={{ marginRight: 8 }}
                  />
                }
              />
            </Box>

            {loadingLo ? (
              <CircularProgress />
            ) : los.length === 0 ? (
              <Typography sx={{ color: theme.palette.text.primary }}>
                {t("learningObjects.empty")}
              </Typography>
            ) : (
              los.map((raw: any, index: number) => {
                const entity = normalizeEnrolledLo(raw);
                const entityType = mapAggregatedType(entity.type);
                const expirationBadge = buildExpirationBadge(
                  entity,
                  String(tEnroll("card.expiration-badge.expired")),
                );
                const cardProps = mapEntityToCard(entity, entityType, {
                  t: tCatalog,
                  onDetails: () => {},
                });
                return (
                  <ListItemCardCst
                    key={entity.id ?? index}
                    {...cardProps}
                    details={{
                      row1: `${tEnroll(
                        "card.enrollment-date",
                      )}: ${formatEnrollmentDate(entity.enrollDate)}`,
                      row2: `${tEnroll(
                        "card.expiration-date",
                      )}: ${formatDate(entity.expirationDate)}`,
                    }}
                    detailsVariant="detailed"
                    badges={[
                      ...(cardProps.badges || []),
                      ...(entity.enrollType
                        ? [{ label: formatEnrollTypeLabel(entity.enrollType) }]
                        : []),
                      ...(expirationBadge ? [expirationBadge] : []),
                    ]}
                    actions={[]}
                    // S05: azioni sempre visibili, disabilitate senza permesso.
                    moreActions={[
                      {
                        label: String(
                          tEnroll("card.actions.edit-group-enrollment"),
                        ),
                        icon: <Pencil size={14} />,
                        onClick: () => setEditGroupLoTarget(entity),
                        disabled: !canUpdateEnrollment,
                      },
                      {
                        label: String(
                          tEnroll("card.actions.delete-group-enrollment"),
                        ),
                        icon: <Trash2 size={14} />,
                        onClick: () => setDeleteLoTarget(entity),
                        disabled: !canUpdateEnrollment,
                      },
                      // {
                      // label: String(tEnroll("card.actions.solicit")),
                      // icon: <IconMegaphone size={14} />,
                      // onClick: () => openSolicit(entity),
                      // disabled: !canSolicit,
                      // },
                    ]}
                  />
                );
              })
            )}

            {loPages > 1 && (
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <PaginatorCst
                  page={loPage}
                  pageSize={loPageSize}
                  totalItems={loTotal}
                  onPageChange={setLoPage}
                  onPageSizeChange={(next) => {
                    setLoPageSize(next);
                    setLoPage(1);
                  }}
                />
              </Box>
            )}
          </Box>
        )}
      </Box>

      <AddUsersToGroupModal
        open={addUsersOpen}
        onClose={() => setAddUsersOpen(false)}
        corporateId={corporateId}
        group={{ id: groupId, name: groupDetail?.name }}
        onSuccess={mutateUsers}
      />

      <UploadModalCst
        open={csvOpen}
        onClose={() => setCsvOpen(false)}
        title={String(tWizard("uploadCsv"))}
        accept=".csv,text/csv"
        banner={{
          title: String(tWizard("csv-modal.banner-title")),
          text: String(tWizard("csv-modal.banner-text")),
          downloadLabel: String(tWizard("csv-modal.banner-download")),
          actionsAlign: "center",
          onDownload: () =>
            downloadFile(
              `${ServicesURL.staticGroups}/static/group/download/template`,
            ),
          secondaryDownloadLabel: String(
            tWizard("csv-modal.banner-download-members"),
          ),
          onSecondaryDownload: () =>
            downloadFile(
              `${ServicesURL.staticGroups}/static/${groupId}/users/external-id`,
            ),
        }}
        onUpload={(file) => {
          const formData = new FormData();
          formData.append("file", file);
          return uploadFile(
            `${ServicesURL.staticGroups}/static/${groupId}`,
            formData,
            MethodType.PUT,
          );
        }}
        onUploaded={() => mutateUsers && mutateUsers()}
      />

      {/* <SolicitConfirmDialog
        target={solicitTarget}
        onClose={() => setSolicitTarget(null)}
      /> */}

      <ConfirmDialogCst
        open={!!deleteLoTarget}
        title={String(tEnroll("card.delete-group-confirm.title"))}
        description={String(
          tEnroll(
            warnDeleteLoProgress
              ? "card.delete-group-confirm.description-progress"
              : "card.delete-group-confirm.description",
          ),
        )}
        confirmLabel={String(tEnroll("card.delete-confirm.confirm"))}
        cancelLabel={String(tEnroll("card.delete-confirm.cancel"))}
        confirmVariant="danger"
        isSubmitting={isDeletingLo}
        onClose={() => setDeleteLoTarget(null)}
        onConfirm={confirmDeleteLo}
      />

      <OutcomeModalV2Cst
        open={showPartialDeleteInfo}
        outcome={{ deleteGroupEnrollment: { code: "OK", message: undefined } }}
        successTitle={tEnroll("card.delete-group-partial.title")}
        description={tEnroll("card.delete-group-partial.description")}
        confirmLabel={tEnroll("card.delete-group-partial.back-to-group")}
        hideCloseButton
        onClose={() => setShowPartialDeleteInfo(false)}
        onConfirm={() => setShowPartialDeleteInfo(false)}
      />

      <ConfirmDialogCst
        open={!!editGroupLoTarget}
        title={String(tEnroll("card.edit-group-confirm.title"))}
        description={String(
          tEnroll("card.edit-group-confirm.description", { groupId }),
        )}
        confirmLabel={String(tEnroll("card.edit-group-confirm.confirm"))}
        cancelLabel={String(tEnroll("card.delete-confirm.cancel"))}
        isSubmitting={isLoadingGroupEnrollment}
        submittingLabel={String(tEnroll("card.edit-group-confirm.loading"))}
        onClose={() => setEditGroupLoTarget(null)}
        onConfirm={openGroupEnrollmentEditor}
      />

      <EnrollLOModal
        open={!!groupEnrollSource}
        onClose={() => setGroupEnrollSource(null)}
        subjectType="group"
        subject={{
          id: Number(groupId),
          name: groupDetail?.name || `ID ${groupId}`,
        }}
        groupType="STATIC"
        corporateId={String(corporateId)}
        onSuccess={() => {
          mutateLos();
          setTimeout(() => mutateLos(), 1500);
          setTimeout(() => mutateLos(), 4000);
          setGroupEnrollSource(null);
        }}
        initialStep="config"
        initialSelection={
          groupEnrollSource
            ? [
                {
                  id: groupEnrollSource.id,
                  kind: groupEnrollSource.entityType,
                  title: groupEnrollSource.title ?? "",
                  entity: {},
                },
              ]
            : []
        }
        initialConfig={
          groupEnrollSource
            ? buildConfigFromEnrollment(groupEnrollSource)
            : undefined
        }
        extraDisabledEnrollTypes={disabledByPriority(
          groupEnrollSource?.enrollType,
        )}
      />
    </AnimationFadeIn>
  );
};

export default StaticGroupDetail;
